/**
 * The selection criteria this report reviews queries against, and the arithmetic that applies
 * them.
 *
 * Every one of them is a product choice made in the browser over rows Search Console already
 * returned. None is a Search Analytics dimension filter, so changing one never costs a request —
 * which is why the controls say "re-filter" rather than "fetch", and why a candidate list is a
 * reading of the retrieved rows rather than a different question put to Google.
 *
 * What comes out is a review queue. A query inside the band is one a human might look at; it is
 * not a forecast, a difficulty score, or a claim that moving it is possible. The reason each row
 * was selected travels with it for exactly that purpose: the queue has to be able to show its
 * working.
 */

/** The two ways a candidate is chosen. Both read the same retrieved rows. */
export const PRESET = { NEARER_TOP: 'nearer_top', LOW_CTR: 'low_ctr' };

/**
 * Starting criteria: average position 4–20 with at least 1,000 impressions, and — for the low-CTR
 * preset — a CTR at or under 2%. The ceiling is editable rather than fixed, because there is no
 * benchmark CTR that holds across surfaces, markets and query intents, and presenting one as a
 * threshold would be inventing a standard.
 */
export const DEFAULT_THRESHOLDS = {
    minPosition: 4,
    maxPosition: 20,
    minImpressions: 1000,
    maxCtr: 0.02,
};

const number = (value) => {
    const parsed = typeof value === 'number' ? value : parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Why this draft cannot be applied, as a list of sentences, or an empty list.
 *
 * Invalid input is reported rather than coerced: silently clamping a reversed band or a negative
 * floor would hand back a candidate list selected by criteria the user never chose, which is the
 * one thing a review queue must not do.
 */
export function thresholdErrors(draft, t) {
    const errors = [];

    const minPosition = number(draft.minPosition);
    const maxPosition = number(draft.maxPosition);
    const minImpressions = number(draft.minImpressions);
    const maxCtr = number(draft.maxCtr);

    if (minPosition === null || maxPosition === null) {
        errors.push(t('tools.search_opportunities.errors.position_required'));
    } else if (minPosition < 1 || maxPosition < 1) {
        errors.push(t('tools.search_opportunities.errors.position_positive'));
    } else if (minPosition > maxPosition) {
        errors.push(t('tools.search_opportunities.errors.position_order'));
    }

    if (minImpressions === null || minImpressions < 0) {
        errors.push(t('tools.search_opportunities.errors.impressions_nonnegative'));
    }

    if (draft.preset === PRESET.LOW_CTR) {
        if (maxCtr === null || maxCtr < 0 || maxCtr > 1) {
            errors.push(t('tools.search_opportunities.errors.ctr_range'));
        }
    }

    return errors;
}

/** The draft as numbers, once it is known to be valid. */
export function resolveThresholds(draft) {
    return {
        preset: draft.preset === PRESET.LOW_CTR ? PRESET.LOW_CTR : PRESET.NEARER_TOP,
        minPosition: number(draft.minPosition),
        maxPosition: number(draft.maxPosition),
        minImpressions: number(draft.minImpressions),
        maxCtr: number(draft.maxCtr),
    };
}

/** Whether one retrieved row sits inside the position band. */
function inBand(row, thresholds) {
    if (row.position === null || row.position === undefined) return false;
    return row.position >= thresholds.minPosition && row.position <= thresholds.maxPosition;
}

/** Whether one retrieved row clears the impression floor. */
function overFloor(row, thresholds) {
    return (row.impressions ?? 0) >= thresholds.minImpressions;
}

/**
 * Whether one retrieved row is at or under the CTR ceiling.
 *
 * A row with no impressions has no CTR to test — Search Console reports 0 there, which would read
 * as a perfect candidate — so it is excluded rather than counted as the lowest CTR on the page.
 */
function underCeiling(row, thresholds) {
    if (row.ctr === null || row.ctr === undefined) return false;
    return row.ctr <= thresholds.maxCtr;
}

/** Whether a row is a candidate at all, under the preset in force. */
export function isCandidate(row, thresholds) {
    if (!inBand(row, thresholds) || !overFloor(row, thresholds)) return false;
    if (thresholds.preset === PRESET.LOW_CTR) return underCeiling(row, thresholds);
    return true;
}

/**
 * The criteria that put a row in the queue, written out.
 *
 * Built per row rather than once per table because a reader checking one candidate should not
 * have to carry the control panel in their head to know why it is there.
 */
export function selectionReason(t, thresholds) {
    const clauses = [
        t('tools.search_opportunities.reasons.position', {
            min: thresholds.minPosition,
            max: thresholds.maxPosition,
        }),
        t('tools.search_opportunities.reasons.impressions', { min: thresholds.minImpressions }),
    ];

    if (thresholds.preset === PRESET.LOW_CTR) {
        clauses.push(
            t('tools.search_opportunities.reasons.ctr', {
                max: (thresholds.maxCtr * 100).toFixed(2).replace(/\.?0+$/, ''),
            }),
        );
    }

    return clauses.join('; ');
}

/**
 * The candidates, ordered by impressions.
 *
 * Impressions is the default order because it is the one figure here that is a count of something
 * that happened, rather than an average over it. Ties break on the query text so the queue is
 * stable between two reads of the same scope — a shifting order would make a review list
 * impossible to work through.
 */
export function selectCandidates(rows, thresholds, t) {
    const reason = selectionReason(t, thresholds);

    return rows
        .filter((row) => isCandidate(row, thresholds))
        .map((row) => ({ ...row, reason }))
        .sort((a, b) => {
            const size = (b.impressions ?? 0) - (a.impressions ?? 0);
            if (size !== 0) return size;
            return a.key < b.key ? -1 : 1;
        });
}

/**
 * How many rows each preset would select from the same retrieved set.
 *
 * Both counts are shown side by side so the coverage block can say what the other reading of the
 * same data would have produced, instead of leaving the impression that the queue on screen is
 * the only one available.
 */
export function presetCounts(rows, thresholds) {
    const nearerTop = { ...thresholds, preset: PRESET.NEARER_TOP };
    const lowCtr = { ...thresholds, preset: PRESET.LOW_CTR };

    return {
        nearerTop: rows.filter((row) => isCandidate(row, nearerTop)).length,
        lowCtr: rows.filter((row) => isCandidate(row, lowCtr)).length,
    };
}
